'use client'

import { useLayoutEffect, useRef, useState } from "react";
import { debounce } from "./debounce";

const DEFAULT_ELLIPSIS = "...";

export function useLineClamp({
	lines = 1,
	text,
}: {
	lines?: number;
	text: string;
}) {
	const textElementRef = useRef<HTMLDivElement>(null);
	const clampedText = useRef<string>("");
	const [isClamped, setIsClamped] = useState<boolean>(false);

	useLayoutEffect(() => {
		function clamp() {
			if (typeof text !== "string" || text.length <= 0) return;
			const updateTextRefs = (newText: string): void => {
				clampedText.current = newText;

				if (textElementRef.current != null) {
					textElementRef.current.innerText = newText;
				}
			};

			updateTextRefs(".");

			const parentWidth =
				textElementRef.current?.parentElement?.offsetWidth ?? 0;
			const lineHeight = textElementRef.current?.offsetHeight ?? 0;
			const maxHeight = lineHeight * lines + 1;

			const ellipsisLength = 3;

			let start = 0;
			let end = text.length;

			while (start <= end) {
				const slicedText = text.slice(0, end);
				updateTextRefs(slicedText);

				if (
					(textElementRef.current?.clientHeight ?? 0) <= maxHeight &&
					(textElementRef.current?.clientWidth ?? 0) <= parentWidth
				) {
					break;
				}

				end--;
			}
			if (end === text.length) {
				setIsClamped(false);
				return;
			}
			const textPlusEllipsis =
				text.slice(0, Math.max(end - ellipsisLength, 0)).trim() +
				DEFAULT_ELLIPSIS;

			updateTextRefs(textPlusEllipsis);
			setIsClamped(true);
		}

		clamp();

		const debouncedClamp = debounce(clamp, 100);

		window.addEventListener("resize", debouncedClamp);

		return () => {
			debouncedClamp.cancel();
			window.removeEventListener("resize", debouncedClamp);
		};
	}, [lines, text]);

	return { containerRef: textElementRef, isClamped };
}
